// =============================================================
// MONOCLE LENS MAP
// Single-eye prescription selector for Monocle Madness™
// Dawn 3.0 | AI Order Form theme
//
// Features:
//   - Intent: Fashion lenses | Prescription lenses
//   - Prescription type: Distance / Reading / Varifocal / Bifocal
//   - Lens type: Clear / Blue Light / Polarised / Sun / Transitions
//   - Colour swatch (where applicable)
//   - Which eye: Right (OD) | Left (OS)  → line-item property "Eye"
//   - Rx fields: SPH, CYL, Axis, No CYL, ADD, Prism
//   - Prescription upload + AI parse
//   - ATC gating until all required fields are complete
//
// No Ready Readers. No PD. No Frame Only. No dual-eye.
// =============================================================

(function () {
  "use strict";

  // ─── Guard against double-init ──────────────────────────────
  if (window.__MONOCLE_LENS_MAP_BOUND__) return;
  window.__MONOCLE_LENS_MAP_BOUND__ = true;

  // ─── CMM guard: only run on Custom Monocle products ─────────
  // Liquid injects window.__MONOCLE_IS_CMM__ = true on CMM products.
  // If the flag is absent (e.g. cached script on a non-CMM page),
  // bail out immediately so the ATC button is never locked.
  if (!window.__MONOCLE_IS_CMM__) return;

  // ─── Line-item property helpers ─────────────────────────────
  var getAllForms = function () {
    var forms = [];
    document.querySelectorAll('form[action="/cart/add"], form[action*="/cart/add"]').forEach(function (f) {
      forms.push(f);
    });
    return forms;
  };

  // ─── Upload plumbing ────────────────────────────────────────
  // The file input in monocle-lens-selector.liquid carries form="{{ form_id }}"
  // which form-associates it with the product <form> without moving it in the DOM.
  // Dawn's product-form.js uses new FormData(this.form), which picks up all
  // form-associated elements — so the file is sent natively via multipart/form-data.
  // No JS manipulation of the input is required.

  // ─── DOM finders ────────────────────────────────────────────
  var uiRoot = function () {
    return document.querySelector("[data-monocle-lens]") || null;
  };

  var customPanel = function () {
    var r = uiRoot();
    return r ? r.querySelector("[data-custom-panel]") : null;
  };

  var stepEye = function () {
    var p = customPanel();
    return p ? p.querySelector('[data-step="eye"]') : null;
  };

  var stepUse = function () {
    var p = customPanel();
    return p ? p.querySelector('[data-step="use"]') : null;
  };

  var stepType = function () {
    var p = customPanel();
    return p ? p.querySelector('[data-step="type"]') : null;
  };

  var stepColour = function () {
    var p = customPanel();
    return p ? p.querySelector('[data-step="colour"]') : null;
  };

  var swatchGrid = function () {
    var s = stepColour();
    return s ? s.querySelector("[data-monocle-swatch-grid]") : null;
  };

  var stepRx = function () {
    var p = customPanel();
    return p ? p.querySelector('[data-step="rx"]') : null;
  };

  var uploadRow = function () {
    var r = uiRoot();
    return r ? r.querySelector("[data-rx-upload-row]") : null;
  };

  var uploadWrap = function () {
    var r = uiRoot();
    return r ? r.querySelector("[data-rx-upload-wrap]") : null;
  };

  var uploadChooseBtn = function () {
    var r = uiRoot();
    return r ? r.querySelector("[data-rx-upload-choose]") : null;
  };

  var helperEl = function () {
    var r = uiRoot();
    return r ? r.querySelector("[data-monocle-helper]") : null;
  };

  var stickyBar = function () {
    return document.querySelector("[data-monocle-sticky-atc]") || null;
  };

  var summaryEls = function () {
    var r = uiRoot();
    return r ? r.querySelectorAll("[data-monocle-lens-summary-text]") : [];
  };

  // ─── State ──────────────────────────────────────────────────
  var state = {
    intent: null,        // "clear" | "prescription"
    use: null,           // "distance" | "reading" | "varifocal" | "bifocal"
    bifocal_shape: null, // null | "D 28mm" | "R 24mm" — only relevant when use=bifocal
    type: null,          // "clear" | "blue_light" | "polarised" | "sun" | "trans"
    colour: null,        // swatch value string
    rx: {
      eye: null,         // null | "r" | "l"  — must be chosen before upload/autofill
      sph: null,
      cyl: null,
      axis: null,
      no_cyl: false,
      add: null,
      near_sph: null,
      prism_enabled: false,
      prism_h: null,
      base_h: "IN",
      prism_v: null,
      base_v: "UP",
      send_later: false,
      uploaded: false,
      rx_parsed: false
    }
  };

  // Expose state for console debugging
  window.__MONOCLE_STATE__ = state;

  // ─── Swatch sets ────────────────────────────────────────────
  var SWATCH_SETS = {
    blue_light: [
      { value: "Clear", dot: "#eceeeb" },
      { value: "Yellow", dot: "rgba(255,255,0,0.75)" },
      { value: "Red", dot: "rgba(255,0,0,0.72)" }
    ],
    sun: [
      { value: "Grey", dot: "grey" },
      { value: "Brown", dot: "#806666" },
      { value: "G-15 Green", dot: "green" }
    ],
    polarised: [
      { value: "Grey", dot: "grey" },
      { value: "Brown", dot: "#806666" },
      { value: "G-15", dot: "green" }
    ],
    trans: [
      { value: "Grey", dot_from: "#8080800c", dot_to: "grey" },
      { value: "Brown", dot_from: "#80666611", dot_to: "#806666" },
      { value: "S/Green", dot_from: "#dff0df", dot_to: "#0f4d0f" },
      { value: "Sapphire", dot_from: "#2195f311", dot_to: "#2195f3" },
      { value: "Amethyst", dot_from: "#eadcf0", dot_to: "#4a1363" },
      { value: "Amber", dot_from: "#ffe0b3", dot_to: "#9c5f00" },
      { value: "Ruby", dot_from: "#ffd2d2", dot_to: "#7c0000" }
    ]
  };

  var needsColour = function (t) {
    return t === "blue_light" || t === "sun" || t === "polarised" || t === "trans";
  };

  var setLineItemProperty = function (key, value) {
    getAllForms().forEach(function (form) {
      var name = "properties[" + key + "]";
      var el = form.querySelector('input[name="' + name + '"]');
      if (!el) {
        el = document.createElement("input");
        el.type = "hidden";
        el.name = name;
        form.appendChild(el);
      }
      el.value = value != null ? String(value) : "";
    });
  };

  var removeLineItemProperty = function (key) {
    getAllForms().forEach(function (form) {
      var name = "properties[" + key + "]";
      var el = form.querySelector('input[name="' + name + '"]');
      if (el) el.parentNode.removeChild(el);
    });
  };

  // ─── ATC button helpers ─────────────────────────────────────
  var getAtcBtn = function () {
    // Prioritise the product-page submit button by ID to avoid matching the
    // cart drawer checkout button (which also matches generic submit selectors
    // and appears earlier in the DOM).
    var byId = document.querySelector('[id^="ProductSubmitButton"]');
    if (byId) return byId;
    // Fallback: find a button[name="add"] inside a /cart/add form
    var forms = getAllForms();
    for (var i = 0; i < forms.length; i++) {
      var b = forms[i].querySelector('button[name="add"], button[type="submit"]');
      if (b) return b;
    }
    return null;
  };

  var getReadyBanner = function () {
    var r = uiRoot();
    return r ? r.querySelector("[data-monocle-ready-banner]") : null;
  };

  // ── Guard: watch for external scripts re-enabling the button while we have
  //    it locked (e.g. the product-page DOMContentLoaded updateATCState script).
  //    We use a MutationObserver on the button's "disabled" attribute; if the
  //    button is re-enabled while data-monocle-locked is still set, we
  //    immediately re-disable it.
  var _atcObserver = null;
  var _startAtcGuard = function () {
    if (_atcObserver) return; // already watching
    var btn = getAtcBtn();
    if (!btn) return;
    _atcObserver = new MutationObserver(function () {
      var b = getAtcBtn();
      // Dawn re-enabled the button (e.g. after a variant fetch completes).
      // Re-evaluate the full monocle state rather than blindly re-disabling,
      // so the button unlocks correctly when the form is already complete.
      if (b && !b.disabled) {
        // Use setTimeout(0) to let Dawn finish any synchronous post-fetch work
        // (e.g. updating button text) before we evaluate the lock state.
        setTimeout(function () { syncAtcLock(); }, 0);
      }
    });
    _atcObserver.observe(btn, { attributes: true, attributeFilter: ["disabled"] });
  };

  var lockAtc = function (msg) {
    // Lock the main ATC button
    var btn = getAtcBtn();
    if (btn) {
      // Set data-monocle-locked BEFORE disabling so the Liquid MutationObserver
      // sees the lock flag already in place when it fires on the disabled change.
      btn.setAttribute("data-monocle-locked", "1");
      if (msg) btn.setAttribute("data-monocle-lock-reason", msg);
      btn.disabled = true;
      // CSS class lock survives external scripts toggling .disabled
      btn.classList.add("monocle-atc-locked");
    }
    // Also lock the sticky bar if present
    var sticky = document.getElementById("mm-sticky-btn");
    if (sticky) sticky.classList.add("monocle-atc-locked");
    _startAtcGuard();
    // Hide the ready banner
    var banner = getReadyBanner();
    if (banner) banner.hidden = true;
  };

  var unlockAtc = function () {
    // Unlock the main ATC button
    var btn = getAtcBtn();
    if (btn) {
      btn.classList.remove("monocle-atc-locked");
      btn.removeAttribute("data-monocle-locked");
      btn.removeAttribute("data-monocle-lock-reason");
      btn.disabled = false;
    }
    // Also unlock the sticky bar if present
    var sticky = document.getElementById("mm-sticky-btn");
    if (sticky) sticky.classList.remove("monocle-atc-locked");
    // Show the ready banner with animation replay
    var banner = getReadyBanner();
    if (banner) {
      banner.hidden = true;
      void banner.offsetWidth; // force reflow so animation replays
      banner.hidden = false;
    }
  };

  // ─── Summary text ────────────────────────────────────────────
  var setSummaryText = function (text) {
    summaryEls().forEach(function (el) {
      el.textContent = String(text || "");
    });
  };

  var buildSummary = function () {
    if (!state.intent) return "";
    var parts = ["Custom lenses"];
    if (state.intent === "clear") parts.push("Clear fashion");
    if (state.intent === "prescription") parts.push("Prescription");
    if (state.use) {
      var useLabels = { distance: "Distance", reading: "Reading", varifocal: "Varifocal", bifocal: "Bifocal" };
      if (useLabels[state.use]) parts.push(useLabels[state.use]);
    }
    if (state.type) {
      var typeLabels = { clear: "Clear", blue_light: "Blue Light", polarised: "Polarised Sun", sun: "Sunglasses", trans: "Transitions" };
      if (typeLabels[state.type]) parts.push(typeLabels[state.type]);
    }
    if (state.colour) parts.push("Colour: " + state.colour);
    if (state.intent === "prescription" && state.rx.eye) {
      parts.push("Eye: " + (state.rx.eye === "r" ? "Right" : "Left"));
    }
    return parts.join(" \u2022 ");
  };

  // ─── Step visibility ─────────────────────────────────────────
  var showStep = function (el) { if (el) el.hidden = false; };
  var hideStep = function (el) { if (el) el.hidden = true; };

  // ─── Render ──────────────────────────────────────────────────
  var render = function () {
    var root = uiRoot();
    if (!root) return;

    // Custom panel always visible
    var panel = customPanel();
    if (panel) panel.hidden = false;

    // Intent pill states
    root.querySelectorAll("button[data-intent]").forEach(function (b) {
      var on = b.getAttribute("data-intent") === state.intent;
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-pressed", on ? "true" : "false");
    });

    // EYE step — first choice for prescription
    var eyeStep = stepEye();
    if (eyeStep) {
      eyeStep.hidden = state.intent !== "prescription";
            eyeStep.querySelectorAll("button[data-eye]").forEach(function (b) {
        var on = b.getAttribute("data-eye") === state.rx.eye;
        b.classList.toggle("is-active", on);
        b.setAttribute("aria-pressed", on ? "true" : "false");
      });
      // Always keep the Eye line-item property in sync with state so it
      // survives variant switches and section reloads that recreate form inputs.
      if (state.rx.eye) {
        setLineItemProperty("Eye", state.rx.eye === "r" ? "Right" : "Left");
      } else {
        removeLineItemProperty("Eye");
      }
    }

    // USE step — only after eye is chosen
    var useStep = stepUse();
    if (useStep) {
      useStep.hidden = state.intent !== "prescription" || !state.rx.eye;
      if (state.use) {
        useStep.querySelectorAll("button[data-use]").forEach(function (b) {
          var on = b.getAttribute("data-use") === state.use;
          b.classList.toggle("is-active", on);
          b.setAttribute("aria-pressed", on ? "true" : "false");
        });
      }
    }

    // BIFOCAL SHAPE step — show only when use=bifocal (prescription path)
    var bifocalWrap = customPanel() ? customPanel().querySelector('[data-bifocal-shape-wrap]') : null;
    if (bifocalWrap) {
      var showBifocalShape = state.intent === "prescription" && !!state.rx.eye && state.use === "bifocal";
      bifocalWrap.hidden = !showBifocalShape;
      if (showBifocalShape) {
        bifocalWrap.querySelectorAll('[data-bifocal-shape-btn]').forEach(function (btn) {
          var on = btn.getAttribute('data-value') === state.bifocal_shape;
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
    }

    // TYPE step — show when intent=clear, or intent=prescription + eye + use chosen
    // For bifocal, also require shape to be chosen first.
    var typeStep = stepType();
    if (typeStep) {
      var showType = state.intent === "clear" || (state.intent === "prescription" && !!state.rx.eye && !!state.use &&
        (state.use !== "bifocal" || !!state.bifocal_shape));
      typeStep.hidden = !showType;
      if (showType) {
        // For R 24mm bifocal, hide polarised and transitions (no products available)
        var isR24 = state.use === "bifocal" && state.bifocal_shape === "R 24mm";
        typeStep.querySelectorAll("button[data-type]").forEach(function (b) {
          var t = b.getAttribute("data-type");
          var restricted = isR24 && (t === "polarised" || t === "trans");
          b.style.display = restricted ? "none" : "";
          // If a restricted type was previously selected, clear it
          if (restricted && state.type === t) {
            state.type = null;
            state.colour = null;
            removeLineItemProperty("Lens type");
            removeLineItemProperty("Lens colour");
          }
          if (state.type) {
            var on = t === state.type;
            b.classList.toggle("is-active", on);
            b.setAttribute("aria-pressed", on ? "true" : "false");
          }
        });
      }
    }

    // COLOUR step — show when type needs colour
    var colourStep = stepColour();
    if (colourStep) {
      var showColour = !!state.type && needsColour(state.type);
      colourStep.hidden = !showColour;
      if (showColour && state.colour) {
        colourStep.querySelectorAll("button[data-swatch]").forEach(function (b) {
          var on = b.getAttribute("data-swatch") === state.colour;
          b.classList.toggle("is-active", on);
          b.setAttribute("aria-pressed", on ? "true" : "false");
        });
      }
    }

    // RX step — show when prescription + eye + use + type chosen
    var rxStep = stepRx();
    if (rxStep) {
      var showRx = state.intent === "prescription" && !!state.rx.eye && !!state.use &&
        (state.use !== "bifocal" || !!state.bifocal_shape) && !!state.type;
      rxStep.hidden = !showRx;

      if (showRx) {
        // Eye radio
        rxStep.querySelectorAll('input[data-rx="eye"]').forEach(function (radio) {
          radio.checked = radio.value === state.rx.eye;
        });

        // No CYL checkbox
        var noCylCb = rxStep.querySelector('[data-rx-toggle="no_cyl"]');
        if (noCylCb) noCylCb.checked = !!state.rx.no_cyl;

        // CYL + Axis row visibility
        var cylAxisRow = rxStep.querySelector("[data-rx-cylaxis]");
        if (cylAxisRow) cylAxisRow.hidden = !!state.rx.no_cyl;

        // ADD row — only for varifocal/bifocal
        var addRow = rxStep.querySelector("[data-rx-add-row]");
        if (addRow) {
          var isMulti = state.use === "varifocal" || state.use === "bifocal";
          addRow.hidden = !isMulti;
        }

        // Prism fields
        var prismFields = rxStep.querySelector("[data-rx-prism-fields]");
        if (prismFields) prismFields.hidden = !state.rx.prism_enabled;

        // Upload row
        var upRow = uploadRow();
        if (upRow) {
          var needsCol = needsColour(state.type);
          upRow.hidden = needsCol ? !state.colour : false;
        }

        // Send later UI
        var sendLaterCard = rxStep.querySelector("[data-send-later-card]");
        if (sendLaterCard) {
          var cb = sendLaterCard.querySelector('input[data-rx-toggle="send_later"]');
          if (cb) cb.checked = !!state.rx.send_later;
        }

        var rxFieldsWrap = rxStep.querySelector("[data-rx-fields-wrap]");
        if (rxFieldsWrap) {
          var needsCol2 = needsColour(state.type);
          var missingCol = needsCol2 && !state.colour;
          // Hide until: (a) a file has been uploaded AND the worker has finished parsing,
          // OR (b) send_later is chosen. Never show on first appearance.
          var notYetReady = !state.rx.rx_parsed; // covers both "no file yet" and "parsing in progress"
          rxFieldsWrap.hidden = !!state.rx.send_later || missingCol || notYetReady;
        }

        var sendLaterNotice = rxStep.querySelector("[data-send-later-notice]");
        if (sendLaterNotice) sendLaterNotice.hidden = !state.rx.send_later;
      }
    }

    // Helper text
    var helper = helperEl();
    if (helper) {
      helper.hidden = !!state.intent;
    }

    // Sticky ATC bar removed

    // ATC gating
    syncAtcLock();

    // Summary
    setSummaryText(buildSummary());

    // File input is form-associated via form="{{ form_id }}" in the liquid — no JS needed.
  };

  // ─── ATC gating ──────────────────────────────────────────────
  var rxIsComplete = function () {
    if (state.intent !== "prescription") return true;
    if (!state.rx.eye || !state.use || !state.type) return false;

    var hasVal = function (v) { return v != null && String(v).trim() !== ""; };
    var isNum = function (v) {
      var n = parseFloat(String(v != null ? v : "").trim());
      return isFinite(n) && !isNaN(n);
    };

    if (state.rx.send_later) return true;

    if (!hasVal(state.rx.sph)) return false;

    if (!state.rx.no_cyl) {
      if (!hasVal(state.rx.cyl) || !hasVal(state.rx.axis)) return false;
      if (!isNum(state.rx.axis)) return false;
    }

    var isMulti = state.use === "varifocal" || state.use === "bifocal";
    if (isMulti) {
      var addOk = hasVal(state.rx.add) && isNum(state.rx.add);
      var nearOk = hasVal(state.rx.near_sph) && hasVal(state.rx.sph);
      if (!addOk && !nearOk) return false;
    }

    return true;
  };

  var syncAtcLock = function () {
    if (!state.intent) {
      lockAtc("Choose your lens path to continue");
      return;
    }
    if (state.intent === "prescription") {
      if (!state.rx.eye) { lockAtc("Choose which eye this lens is for"); return; }
      if (!state.use) { lockAtc("Choose your prescription type"); return; }
      if (state.use === "bifocal" && !state.bifocal_shape) { lockAtc("Choose your bifocal shape"); return; }
      if (!state.type) { lockAtc("Choose your lens type"); return; }
      if (needsColour(state.type) && !state.colour) { lockAtc("Choose a lens colour"); return; }
      if (!rxIsComplete()) { lockAtc("Complete your prescription"); return; }
    }
    if (state.intent === "clear") {
      if (!state.type) { lockAtc("Choose your lens type"); return; }
      if (needsColour(state.type) && !state.colour) { lockAtc("Choose a lens colour"); return; }
    }
    unlockAtc();
  };

  // ─── Swatch grid builder ─────────────────────────────────────
  var buildSwatchGrid = function (lensType) {
    var grid = swatchGrid();
    if (!grid) return;
    grid.innerHTML = "";
    var swatches = SWATCH_SETS[lensType] || [];
    swatches.forEach(function (s) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "monocle-swatch";
      btn.setAttribute("data-swatch", s.value);
      btn.setAttribute("aria-pressed", state.colour === s.value ? "true" : "false");
      if (state.colour === s.value) btn.classList.add("is-active");

      var dot = document.createElement("span");
      dot.className = "monocle-swatch__dot";
      if (s.dot_from && s.dot_to) {
        dot.style.background = "linear-gradient(135deg, " + s.dot_from + " 50%, " + s.dot_to + " 50%)";
      } else {
        dot.style.background = s.dot;
      }
      btn.appendChild(dot);

      var label = document.createElement("span");
      label.className = "monocle-swatch__label";
      label.textContent = s.value;
      btn.appendChild(label);

      grid.appendChild(btn);
    });
  };

  // ─── Tooltip toggle ──────────────────────────────────────────
  var closeAllTooltips = function () {
    var r = uiRoot();
    if (!r) return;
    r.querySelectorAll("[data-tooltip]").forEach(function (el) { el.hidden = true; });
  };

  var toggleTooltip = function (id) {
    var r = uiRoot();
    if (!r || !id) return;
    var tip = r.querySelector('[data-tooltip="' + id + '"]');
    if (!tip) return;
    var wasHidden = tip.hidden;
    closeAllTooltips();
    tip.hidden = !wasHidden;
    // For the colour tooltip, show only the sub-section matching the current lens type
    if (id === 'lens-colour' && !tip.hidden) {
      var activeType = state.type || '';
      tip.querySelectorAll('[data-colour-tip]').forEach(function (el) {
        if (el.getAttribute('data-colour-tip') === activeType) {
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      });
    }
  };

  // ─── Rx field validation ─────────────────────────────────────
  var validateRxField = function (field, rawValue) {
    if (rawValue === "" || rawValue == null) return null;
    var val = parseFloat(rawValue);
    if (!isFinite(val) || isNaN(val)) return "Please enter a valid number.";
    if (field === "sph") {
      if (val < -20 || val > 20) return "SPH must be between \u221220.00 and +20.00.";
      if (Math.round(val / 0.25) * 0.25 !== Math.round(val * 100) / 100)
        return "SPH must be in 0.25 steps.";
    }
    if (field === "cyl") {
      if (val < -10 || val > 10) return "CYL must be between \u221210.00 and +10.00.";
      if (Math.round(val / 0.25) * 0.25 !== Math.round(val * 100) / 100)
        return "CYL must be in 0.25 steps.";
    }
    if (field === "axis") {
      if (!Number.isInteger(val) || val < 0 || val > 180)
        return "Axis must be a whole number between 0 and 180.";
    }
    return null;
  };

  var setRxFieldError = function (field, msg) {
    var root = uiRoot();
    if (!root) return;
    var errEl = root.querySelector('[data-rx-error][data-field="' + field + '"]');
    var inputEl = root.querySelector('[data-step="rx"] [data-rx="' + field + '"]');
    if (errEl) {
      errEl.textContent = msg || "";
      errEl.hidden = !msg;
    }
    if (inputEl) {
      inputEl.classList.toggle("is-invalid", !!msg);
      if (msg) {
        inputEl.setAttribute("aria-invalid", "true");
      } else {
        inputEl.removeAttribute("aria-invalid");
      }
    }
  };

  // ─── Reset helpers ───────────────────────────────────────────
  var resetRxState = function () {
    state.rx.sph = null;
    state.rx.cyl = null;
    state.rx.axis = null;
    state.rx.no_cyl = false;
    state.rx.add = null;
    state.rx.near_sph = null;
    state.rx.prism_enabled = false;
    state.rx.prism_h = null;
    state.rx.base_h = "IN";
    state.rx.prism_v = null;
    state.rx.base_v = "UP";
    state.rx.send_later = false;
    state.rx.uploaded = false;
    state.rx.rx_parsed = false;
    // Clear Rx fields in the DOM
    var rxStep = stepRx();
    if (rxStep) {
      rxStep.querySelectorAll('[data-rx]').forEach(function (el) {
        if (el.tagName === "INPUT" && el.type !== "radio" && el.type !== "checkbox") el.value = "";
        if (el.tagName === "SELECT") el.selectedIndex = 0;
      });
    }
    // Remove Rx line-item properties
    ["Eye", "Rx SPH", "Rx CYL", "Rx Axis", "Rx ADD",
     "Rx Prism Horizontal", "Rx Base Horizontal",
     "Rx Prism Vertical", "Rx Base Vertical",
     "Prescription upload", "Prescription filename"].forEach(removeLineItemProperty);
  };

  var resetDownstream = function () {
    state.use = null;
    state.bifocal_shape = null;
    state.type = null;
    state.colour = null;
    resetRxState();
    removeLineItemProperty("Lens use");
    removeLineItemProperty("Bifocal shape");
    removeLineItemProperty("Lens type");
    removeLineItemProperty("Lens colour");
  };

  // ─── Prism line-item writes ───────────────────────────────────
  var writePrismProps = function () {
    if (state.rx.prism_enabled) {
      if (state.rx.prism_h) setLineItemProperty("Rx Prism Horizontal", state.rx.prism_h);
      if (state.rx.base_h) setLineItemProperty("Rx Base Horizontal", state.rx.base_h);
      if (state.rx.prism_v) setLineItemProperty("Rx Prism Vertical", state.rx.prism_v);
      if (state.rx.base_v) setLineItemProperty("Rx Base Vertical", state.rx.base_v);
    } else {
      removeLineItemProperty("Rx Prism Horizontal");
      removeLineItemProperty("Rx Base Horizontal");
      removeLineItemProperty("Rx Prism Vertical");
      removeLineItemProperty("Rx Base Vertical");
    }
  };

  // ─── Upload / parse helpers ───────────────────────────────────
  var setParseStatus = function (status, message) {
    var root = uiRoot();
    if (!root) return;
    var spinner = root.querySelector("[data-rx-parse-spinner]");
    var notice = root.querySelector("[data-rx-autofill-notice]");
    var noticeText = root.querySelector("[data-rx-autofill-notice-text]");
    if (spinner) spinner.hidden = status !== "loading";
    if (notice) {
      notice.hidden = !status || status === "loading";
      notice.className = "monocle-rx-autofill-notice" + (status ? " monocle-rx-autofill-notice--" + status : "");
      // Set the attribute value so CSS selectors like [data-rx-autofill-notice="warning"] apply
      if (status && status !== "loading") {
        notice.setAttribute("data-rx-autofill-notice", status);
      } else {
        notice.setAttribute("data-rx-autofill-notice", "");
      }
    }
    if (noticeText) noticeText.textContent = message || "";
    // Set icon: success gets a ✓ tick; warning/error icons are handled by CSS ::before
    var iconEl = root.querySelector("[data-rx-autofill-icon]");
    if (iconEl) iconEl.textContent = (status === "success") ? "\u2713" : "";
  };

  var autoFillFromParsed = function (parsed) {
    if (!parsed) return;
    var eye = state.rx.eye; // "r" or "l"
    var currentUse = state.use || "";
    var isReading    = currentUse === "reading";
    var isMultifocal = currentUse === "varifocal" || currentUse === "bifocal";

    // ── Normalise flat vs nested response format ──────────────────────────────
    // Cloudflare worker returns a flat object: { sph_r, cyl_r, axis_r, sph_l, ... }
    // Older nested format:                     { r: { sph, cyl, axis }, l: { ... } }
    // We normalise both into a single eyeData object with short keys.
    var eyeData;
    if (parsed[eye] && typeof parsed[eye] === "object") {
      // Nested format — pick the correct eye sub-object
      eyeData = parsed[eye];
    } else if (parsed.sph_r !== undefined || parsed.sph_l !== undefined) {
      // Flat format from Cloudflare worker — extract the correct eye's fields
      var sfx = "_" + eye; // "_r" or "_l"
      eyeData = {
        sph:     parsed["sph"  + sfx],
        cyl:     parsed["cyl"  + sfx],
        axis:    parsed["axis" + sfx],
        // ADD: prefer per-eye value, fall back to shared add
        add:     parsed["add"  + sfx] != null ? parsed["add" + sfx] : parsed.add,
        no_cyl:  parsed["no_cyl" + sfx] || false,
        prism_h: parsed["prism_h" + sfx],
        base_h:  parsed["base_h"  + sfx],
        prism_v: parsed["prism_v" + sfx],
        base_v:  parsed["base_v"  + sfx]
      };
    } else {
      // Unknown format — try using parsed directly
      eyeData = parsed;
    }

    if (!eyeData) return;

    // ── Helper: set a single Rx input, update state, fire input event ─────────
    var setField = function (k, val) {
      if (val == null) return;
      var numVal = typeof val === "number" ? val : parseFloat(val);
      if (isNaN(numVal)) return;
      state.rx[k] = numVal;
      var el = (stepRx() || document).querySelector('[data-rx="' + k + '"]');
      if (el) {
        el.value = numVal.toFixed(k === "axis" ? 0 : 2);
        el.dispatchEvent(new Event("input", { bubbles: true }));
      }
    };

    // ── Resolve ADD for this eye ──────────────────────────────────────────────
    var addVal = eyeData.add != null ? eyeData.add : null;

    // ── SPH — reading correction ──────────────────────────────────────────────
    // Opticians write a DISTANCE prescription + ADD on the same line.
    // For a reading-only lens the correct power is: SPH + ADD.
    // For varifocal/bifocal we keep the distance SPH and use ADD separately.
    // For distance we use SPH as-is.
    if (isReading) {
      var distSph = eyeData.sph != null ? eyeData.sph : null;
      if (distSph !== null && addVal !== null) {
        // Combine distance SPH + ADD → reading SPH
        setField("sph", parseFloat((distSph + addVal).toFixed(2)));
      } else if (distSph !== null) {
        // No ADD on the prescription — use SPH as written (reading power)
        setField("sph", distSph);
      }
      // Do NOT populate the ADD field for reading lenses — it has already been
      // folded into SPH above and there is no separate ADD input shown.
    } else {
      // Distance or multifocal — use distance SPH as-is
      setField("sph", eyeData.sph);
    }

    // ── CYL & Axis ────────────────────────────────────────────────────────────
    setField("cyl",  eyeData.cyl);
    setField("axis", eyeData.axis);

    // ── ADD / near_sph (varifocal / bifocal only) ─────────────────────────────
    // For varifocal/bifocal: populate the near_sph field (= distance SPH + ADD).
    // The near_sph input handler computes state.rx.add = near_sph - sph,
    // so firing an "input" event on near_sph is sufficient to set state.rx.add.
    // For reading: ADD has already been folded into SPH — do NOT populate near_sph.
    if (isMultifocal && addVal !== null && eyeData.sph != null) {
      var nearSphVal = parseFloat((eyeData.sph + addVal).toFixed(2));
      setField("near_sph", nearSphVal);
    }

    // ── Handle no_cyl ─────────────────────────────────────────────────────────
    if (eyeData.no_cyl) {
      state.rx.no_cyl = true;
      var noCylCb = (stepRx() || document).querySelector('[data-rx-toggle="no_cyl"]');
      if (noCylCb) {
        noCylCb.checked = true;
        noCylCb.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    // ── Handle prism fields ───────────────────────────────────────────────────
    var hasPrism = eyeData.prism_h != null || eyeData.prism_v != null;
    if (hasPrism) {
      state.rx.prism_enabled = true;

      var prismToggle = (stepRx() || document).querySelector('[data-rx-toggle="prism"]');
      if (prismToggle) {
        prismToggle.checked = true;
        prismToggle.dispatchEvent(new Event("change", { bubbles: true }));
      }

      if (eyeData.prism_h != null) {
        state.rx.prism_h = eyeData.prism_h;
        var phEl = (stepRx() || document).querySelector('[data-rx="prism_h"]');
        if (phEl) {
          phEl.value = Number(eyeData.prism_h).toFixed(2);
          phEl.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }

      if (eyeData.base_h) {
        state.rx.base_h = eyeData.base_h;
        var bhEl = (stepRx() || document).querySelector('[data-rx="base_h"]');
        if (bhEl) {
          bhEl.value = eyeData.base_h;
          bhEl.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      if (eyeData.prism_v != null) {
        state.rx.prism_v = eyeData.prism_v;
        var pvEl = (stepRx() || document).querySelector('[data-rx="prism_v"]');
        if (pvEl) {
          pvEl.value = Number(eyeData.prism_v).toFixed(2);
          pvEl.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }

      if (eyeData.base_v) {
        state.rx.base_v = eyeData.base_v;
        var bvEl = (stepRx() || document).querySelector('[data-rx="base_v"]');
        if (bvEl) {
          bvEl.value = eyeData.base_v;
          bvEl.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    }

    render();
    resolveVariant();
  };

  // ─── Swatch builder ───────────────────────────────────────────
  var buildSwatchGrid = function (type) {
    var grid = swatchGrid();
    if (!grid) return;
    grid.innerHTML = "";
    if (!needsColour(type)) return;

    var list = SWATCH_SETS[type] || [];
    list.forEach(function (s) {
      var item = document.createElement("div");
      item.className = "monocle-swatch-item";

      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "monocle-swatch-btn";
      btn.setAttribute("data-swatch", s.value);

      var dot = document.createElement("span");
      dot.className = "monocle-swatch-dot";
      if (s.dot_from && s.dot_to) {
        dot.style.background = "linear-gradient(135deg, " + s.dot_from + " 0%, " + s.dot_to + " 100%)";
      } else {
        dot.style.background = s.dot;
      }
      btn.appendChild(dot);

      var label = document.createElement("div");
      label.className = "monocle-swatch-label";
      label.textContent = s.value;

      item.appendChild(btn);
      item.appendChild(label);
      grid.appendChild(item);
    });
  };

  // ─── Variant selection ────────────────────────────────────────
  var selectVariantById = function (variantId) {
    if (!variantId) return false;

    var changed = false;

    // 1. Try Dawn's native variant-selects (radio pills)
    var variants = window.__MONOCLE_VARIANTS__;
    var targetVariant = variants ? variants.find(function(v) { return String(v.id) === String(variantId); }) : null;
    
    if (targetVariant) {
      // Set the radio checked visually — but do NOT dispatch a change event.
      // Firing change on a variant-selects radio triggers Dawn's handleOptionValueChange
      // → resetProductFormState() → toggleSubmitButton(true) → async variant fetch,
      // which races against our ATC lock and causes the button to stay locked even
      // when the monocle form is complete.
      var radio = document.querySelector('variant-selects input[type="radio"][value="' + targetVariant.title + '"]');
      if (radio && !radio.checked) {
        radio.checked = true;
        changed = true;
      }
    }

    // 2. Update the hidden input[name="id"] directly (no change event needed —
    // Dawn's product-form.js reads this value at submit time via new FormData(form)).
    var idInputs = document.querySelectorAll('input[name="id"]');
    idInputs.forEach(function(inp) {
      if (String(inp.value) !== String(variantId)) {
        inp.value = String(variantId);
        changed = true;
      }
    });

    // 3. Update the price display directly so the customer sees the correct price
    // without triggering Dawn's full variant-fetch cycle.
    // Dawn's price element has id="price-{sectionId}" and the price-item span
    // inside it holds the formatted price text.
    if (targetVariant && targetVariant.price != null) {
      // price is in cents (e.g. 6499 = £64.99)
      var priceInUnits = Number(targetVariant.price) / 100;
      // Use the store's currency code from Shopify's global if available
      var currencyCode = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
      var formatted;
      try {
        formatted = new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(priceInUnits);
      } catch (e) {
        formatted = currencyCode + ' ' + priceInUnits.toFixed(2);
      }
      // Find the Dawn price element — id starts with "price-"
      var priceEl = document.querySelector('[id^="price-"]');
      if (priceEl) {
        // Update the price-item--regular span (regular price)
        var priceItemEl = priceEl.querySelector('.price-item--regular');
        if (priceItemEl) {
          priceItemEl.textContent = formatted;
        }
        // Also update compare-at price item if present
        var compareEl = priceEl.querySelector('.price-item--sale');
        if (compareEl) {
          compareEl.textContent = formatted;
        }
        // Remove hidden class so price is visible
        priceEl.classList.remove('hidden');
      }
    }

    return changed;
  };

  var resolveVariant = function () {
    var variants = window.__MONOCLE_VARIANTS__;
    if (!Array.isArray(variants) || !state.type) return;

    // Helper to select variant by its index in the array
    var selectByIndex = function (idx) {
      if (variants[idx]) selectVariantById(variants[idx].id);
    };

    // Calculate maximum absolute power across SPH and CYL for the single eye
    var sph = Math.abs(Number(state.rx.sph || 0));
    var cyl = Math.abs(Number(state.rx.cyl || 0));
    var add = Number(state.rx.add || 0);
    var tot = Math.abs(Number(state.rx.sph || 0) + Number(state.rx.cyl || 0));
    var maxPower = Math.max(tot, sph, cyl);

    var use = state.use;
    var type = state.type;
    var shape = state.bifocal_shape || "D 28mm"; // default D 28mm for safety

    // ─── Clear fashion ───────────────────────────────────────────────────
    // No prescription — use maxPower = 0 so the lowest-power variant is always
    // selected (index 0 for clear, 3 for sun, etc.), matching the prescription
    // path logic with a plano (zero-power) lens.
    if (state.intent === "clear") {
      if (type === "clear") {
        selectByIndex(0);
      } else if (type === "sun") {
        selectByIndex(3);
      } else if (type === "trans") {
        selectByIndex(5);
      } else if (type === "blue_light") {
        selectByIndex(7);
      } else if (type === "polarised") {
        selectByIndex(10);
      }
      return;
    }

    // ─── Distance / Reading / Near Add ─────────────────────────────────
    if (use === "distance" || use === "reading" || use === "nearadd") {
      if (type === "clear") {
        if (maxPower <= 2) selectByIndex(0);
        else if (maxPower <= 4.75) selectByIndex(1);
        else if (maxPower <= 10) selectByIndex(2);
      } else if (type === "sun") {
        if (maxPower <= 2) selectByIndex(3);
        else if (maxPower <= 8) selectByIndex(4);
      } else if (type === "trans") {
        if (maxPower <= 4) selectByIndex(5);
        else if (maxPower <= 8) selectByIndex(6);
      } else if (type === "blue_light") {
        if (maxPower <= 3) selectByIndex(7);
        else if (maxPower <= 6) selectByIndex(8);
        else if (maxPower <= 7.5) selectByIndex(9);
      } else if (type === "polarised") {
        if (maxPower <= 3) selectByIndex(10);
        else if (maxPower <= 6) selectByIndex(11);
      }
    }

    // ─── Bifocal ───────────────────────────────────────────────────
    else if (use === "bifocal") {
      var isD28 = shape === "D 28mm";
      if (type === "clear") {
        if (isD28) {
          if (maxPower <= 2) selectByIndex(12);
          else if (maxPower <= 6) selectByIndex(13);
        } else {
          // R 24mm: single power band
          if (maxPower <= 6) selectByIndex(14);
        }
      } else if (type === "sun") {
        if (isD28) {
          if (maxPower <= 2) selectByIndex(15);
          else if (maxPower <= 6) selectByIndex(16);
        } else {
          // R 24mm: single power band
          if (maxPower <= 6) selectByIndex(17);
        }
      } else if (type === "trans") {
        // Transitions: D 28mm only (no R 24mm product)
        if (isD28) {
          if (maxPower <= 6.5 && add <= 3) selectByIndex(18);
        }
      } else if (type === "blue_light") {
        if (isD28) {
          if (maxPower <= 7.5 && add <= 3.5) selectByIndex(19);
        } else {
          // R 24mm: single power band
          if (maxPower <= 7.5) selectByIndex(30);
        }
      } else if (type === "polarised") {
        // Polarised: D 28mm only (no R 24mm product)
        if (isD28) {
          if (maxPower <= 6) selectByIndex(20);
        }
      }
    }

    // ─── Varifocal ─────────────────────────────────────────────────
    else if (use === "varifocal") {
      if (type === "clear") {
        if (maxPower <= 2) selectByIndex(21);
        else if (maxPower <= 4.75) selectByIndex(22);
        else if (maxPower <= 8) selectByIndex(23);
      } else if (type === "sun") {
        if (maxPower <= 3) selectByIndex(24);
        else if (maxPower <= 6) selectByIndex(25);
      } else if (type === "trans") {
        if (maxPower <= 3) selectByIndex(26);
        else if (maxPower <= 6) selectByIndex(27);
      } else if (type === "polarised") {
        if (maxPower <= 3) selectByIndex(28);
        else if (maxPower <= 6) selectByIndex(29);
      } else if (type === "blue_light") {
        if (maxPower <= 3) selectByIndex(31);
        else if (maxPower <= 6) selectByIndex(32);
      }
    }
  };

  // ─── Click handler ────────────────────────────────────────────
  document.addEventListener("click", function (e) {
    var root = uiRoot();
    if (!root || !root.contains(e.target)) return;

    // Tooltip toggle
    var tipBtn = e.target.closest("[data-tip]");
    if (tipBtn) {
      e.preventDefault();
      toggleTooltip(tipBtn.getAttribute("data-tip"));
      return;
    }

    // Close tooltip on click outside
    if (!e.target.closest("[data-tooltip]") && !e.target.closest("[data-tip]")) {
      closeAllTooltips();
    }

    // Intent buttons
    var intentBtn = e.target.closest("button[data-intent]");
    if (intentBtn) {
      e.preventDefault();
      var nextIntent = intentBtn.getAttribute("data-intent");
      if (nextIntent !== state.intent) {
        state.intent = nextIntent;
        resetDownstream();
        setLineItemProperty("Lens intent", nextIntent === "clear" ? "Clear fashion" : "Prescription");
      }
      render();
      return;
    }

    // Eye buttons (prescription only — first step)
    var eyeBtn = e.target.closest("button[data-eye]");
    if (eyeBtn && eyeBtn.closest('[data-step="eye"]')) {
      e.preventDefault();
      var nextEye = eyeBtn.getAttribute("data-eye");
      if (nextEye !== state.rx.eye) {
        state.rx.eye = nextEye;
        // Reset downstream (use, type, colour, Rx values) when eye changes
        state.use = null;
        state.type = null;
        state.colour = null;
        resetRxState();
        setLineItemProperty("Eye", nextEye === "r" ? "Right" : "Left");
        removeLineItemProperty("Lens use");
        removeLineItemProperty("Lens type");
        removeLineItemProperty("Lens colour");
      }
      render();
      return;
    }

    // Use buttons
    var useBtn = e.target.closest("button[data-use]");
    if (useBtn && useBtn.closest('[data-step="use"]')) {
      e.preventDefault();
      var nextUse = useBtn.getAttribute("data-use");
      if (nextUse !== state.use) {
        state.use = nextUse;
        state.bifocal_shape = null; // reset shape when use changes
        state.type = null;
        state.colour = null;
        resetRxState();
        setLineItemProperty("Lens use", nextUse);
        removeLineItemProperty("Bifocal shape");
        removeLineItemProperty("Lens type");
        removeLineItemProperty("Lens colour");
      }
      render();
      return;
    }

    // Bifocal shape buttons
    var bifocalShapeBtn = e.target.closest('[data-bifocal-shape-btn]');
    if (bifocalShapeBtn) {
      e.preventDefault();
      var nextShape = bifocalShapeBtn.getAttribute('data-value');
      if (nextShape !== state.bifocal_shape) {
        state.bifocal_shape = nextShape;
        // If switching to R 24mm and a restricted type is selected, clear it
        if (nextShape === 'R 24mm' && (state.type === 'polarised' || state.type === 'trans')) {
          state.type = null;
          state.colour = null;
          removeLineItemProperty('Lens type');
          removeLineItemProperty('Lens colour');
        }
        setLineItemProperty('Bifocal shape', nextShape);
        // Update the hidden input in the liquid
        var shapeInput = (customPanel() || document).querySelector('[data-bifocal-shape]');
        if (shapeInput) shapeInput.value = nextShape;
      }
      resolveVariant();
      render();
      return;
    }

    // Type buttons
    var typeBtn = e.target.closest("button[data-type]");
    if (typeBtn && typeBtn.closest('[data-step="type"]')) {
      e.preventDefault();
      var nextType = typeBtn.getAttribute("data-type");
      if (nextType !== state.type) {
        state.type = nextType;
        state.colour = null;
        removeLineItemProperty("Lens colour");
        setLineItemProperty("Lens type", nextType);
        if (needsColour(nextType)) {
          buildSwatchGrid(nextType);
        }
      }
      resolveVariant();
      render();
      return;
    }

    // Colour swatches
    var swatchBtn = e.target.closest("button[data-swatch]");
    if (swatchBtn) {
      e.preventDefault();
      state.colour = swatchBtn.getAttribute("data-swatch");
      setLineItemProperty("Lens colour", state.colour);
      resolveVariant();
      render();
      return;
    }

    // Upload choose button / label — label[for] handles the file dialog natively,
    // so we only need to prevent default on button elements (not labels)
    var uploadTrigger = e.target.closest("[data-rx-upload-choose]");
    if (uploadTrigger) {
      // If it's a label, the browser handles opening the file dialog via for="..." — do nothing
      // If it's a button (fallback), manually trigger the file input
      if (uploadTrigger.tagName !== 'LABEL') {
        e.preventDefault();
        var fileInput = (uiRoot() || document).querySelector("[data-rx-upload-input]");
        if (fileInput) fileInput.click();
      }
      return;
    }

    // Nudge buttons
    var nudgeBtn = e.target.closest("button[data-rx-nudge]");
    if (nudgeBtn) {
      e.preventDefault();
      var field = nudgeBtn.getAttribute("data-rx-nudge");
      var dir = parseInt(nudgeBtn.getAttribute("data-dir"), 10) || 1;
      var input = nudgeBtn.parentElement.querySelector('input[data-rx="' + field + '"]');
      if (input) {
        var step = parseFloat(input.getAttribute("step")) || 1;
        var min = parseFloat(input.getAttribute("min"));
        var max = parseFloat(input.getAttribute("max"));
        var current = parseFloat(input.value) || 0;
        var next = current + (dir * step);
        if (!isNaN(min) && next < min) next = min;
        if (!isNaN(max) && next > max) next = max;
        // Format to 2 decimal places if step is a decimal
        if (step % 1 !== 0) {
          input.value = next.toFixed(2);
        } else {
          input.value = next;
        }
        // Trigger input event to update state and line items
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
      return;
    }
  }, true);

  // ─── Input/change handler ─────────────────────────────────────
  document.addEventListener("change", function (e) {
    // File upload
    var fileInput = e.target.closest && e.target.closest("[data-rx-upload-input]");
    if (fileInput) {
      var file = fileInput.files[0];
      if (!file) {
        state.rx.uploaded = false;
        state.rx.rx_parsed = false;
        var nameEl = (uiRoot() || document).querySelector("[data-rx-upload-filename]");
        if (nameEl) {
          nameEl.textContent = "";
          nameEl.hidden = true;
        }
        removeLineItemProperty("Prescription upload");
        removeLineItemProperty("Prescription filename");
        setParseStatus(null);
        render();
        return;
      }

      state.rx.uploaded = true;
      state.rx.rx_parsed = false; // hide Rx form until worker responds
      state.rx.send_later = false;
      var cb = (uiRoot() || document).querySelector('input[data-rx-toggle="send_later"]');
      if (cb) cb.checked = false;

      var nameEl2 = (uiRoot() || document).querySelector("[data-rx-upload-filename]");
      if (nameEl2) {
        nameEl2.textContent = file.name;
        nameEl2.hidden = false;
      }

      setLineItemProperty("Prescription upload", "YES");
      setLineItemProperty("Prescription filename", file.name);

      // Trigger the AI parse worker if available
      // The worker might be registered as __JOIUSS_RX_WORKER__ or __MONOCLE_RX_WORKER__
      var worker = window.__MONOCLE_RX_WORKER__ || window.__JOIUSS_RX_WORKER__;
      
      if (worker) {
        setParseStatus("loading", "Reading your prescription...");
        worker(file, function (err, parsed, confidence, warnings) {
          state.rx.rx_parsed = true; // worker responded — reveal the Rx form
          if (err) {
            setParseStatus("error", "We couldn't read that clearly. Please enter it manually below.");
          } else {
            var isLowConfidence = confidence === "low" || (Array.isArray(warnings) && warnings.length > 0);
            if (isLowConfidence) {
              setParseStatus("warning",
                "We’ve done our best to read your prescription. Our optometrist will review and ensure the powers are correct before your lenses are made.");
            } else {
              setParseStatus("success", "Prescription read successfully. Please check the values below.");
            }
            autoFillFromParsed(parsed);
          }
          render(); // re-render to show the Rx form
        });
      } else {
        // No worker — reveal form immediately so customer can type manually
        state.rx.rx_parsed = true;
        setParseStatus("success", "File attached. You can also enter the values manually below if you prefer.");
      }

      render();
      return;
    }

    // Send later toggle
    var sendLater = e.target.closest && e.target.closest('input[data-rx-toggle="send_later"]');
    if (sendLater) {
      state.rx.send_later = !!sendLater.checked;
      if (state.rx.send_later) {
        state.rx.uploaded = false;
        var fIn = (uiRoot() || document).querySelector("[data-rx-upload-input]");
        if (fIn) fIn.value = "";
        var nameEl3 = (uiRoot() || document).querySelector("[data-rx-upload-filename]");
        if (nameEl3) {
          nameEl3.textContent = "";
          nameEl3.hidden = true;
        }
        removeLineItemProperty("Prescription upload");
        removeLineItemProperty("Prescription filename");
        setLineItemProperty("Prescription", "Customer will send later");
      } else {
        removeLineItemProperty("Prescription");
      }
      render();
      return;
    }
  }, true);

  document.addEventListener("input", function (e) {
    var rxInput = e.target.closest && e.target.closest('[data-step="rx"] [data-rx]');
    if (!rxInput) return;

    var k = rxInput.getAttribute("data-rx");

    // Eye toggle
    if (k === "eye") {
      // eye is now set via button click in the eye step, not via radio input
      return;
    }

    // Rx fields
    if (k === "sph" || k === "cyl" || k === "axis" || k === "near_sph" || k === "add") {
      state.rx[k] = rxInput.value;
      if (k === "sph" || k === "cyl" || k === "axis") {
        var errMsg = validateRxField(k, rxInput.value);
        setRxFieldError(k, errMsg);
      }
      if (k === "sph") setLineItemProperty("Rx SPH", rxInput.value || "");
      if (k === "cyl" && !state.rx.no_cyl) setLineItemProperty("Rx CYL", rxInput.value || "");
      if (k === "axis" && !state.rx.no_cyl) setLineItemProperty("Rx Axis", rxInput.value || "");
      if (k === "add") setLineItemProperty("Rx ADD", rxInput.value || "");
      if (k === "near_sph") {
        var dist = Number(state.rx.sph || 0);
        var near = Number(rxInput.value || 0);
        var add = near - dist;
        if (isFinite(add) && Math.abs(add) > 0) {
          state.rx.add = Number(add.toFixed(2));
          setLineItemProperty("Rx ADD", state.rx.add.toFixed(2));
        } else {
          state.rx.add = null;
          removeLineItemProperty("Rx ADD");
        }
      }
    }

    // Prism fields
    if (k === "prism_h" || k === "base_h" || k === "prism_v" || k === "base_v") {
      state.rx[k] = rxInput.value;
      writePrismProps();
    }

    resolveVariant();
    syncAtcLock();
    setSummaryText(buildSummary());
  });

  document.addEventListener("change", function (e) {
    var root = uiRoot();
    if (!root || !root.contains(e.target)) return;

    // No CYL toggle
    var noCylCb = e.target.closest('[data-rx-toggle="no_cyl"]');
    if (noCylCb) {
      state.rx.no_cyl = noCylCb.checked;
      if (state.rx.no_cyl) {
        state.rx.cyl = null;
        state.rx.axis = null;
        removeLineItemProperty("Rx CYL");
        removeLineItemProperty("Rx Axis");
        setRxFieldError("cyl", null);
        setRxFieldError("axis", null);
      }
      render();
      return;
    }

    // Send later toggle
    var sendLaterCb = e.target.closest('[data-rx-toggle="send_later"]');
    if (sendLaterCb) {
      state.rx.send_later = sendLaterCb.checked;
      setLineItemProperty("Prescription send later", state.rx.send_later ? "Yes" : "No");
      render();
      return;
    }

    // Prism toggle
    var prismToggle = e.target.closest('[data-rx-toggle="prism"]');
    if (prismToggle) {
      state.rx.prism_enabled = prismToggle.checked;
      writePrismProps();
      render();
      return;
    }
    // Note: file upload is handled by the earlier capture-phase change listener above.
  });

  // ─── Shopify section events ────────────────────────────────────
  document.addEventListener("shopify:section:load", render);
  document.addEventListener("shopify:section:select", render);
  document.addEventListener("shopify:block:select", render);

  // ─── Variant bootstrap ────────────────────────────────────────
  (function () {
    // Read variants from the Liquid-injected JSON script tag
    if (!Array.isArray(window.__MONOCLE_VARIANTS__)) {
      var jsonEl = document.querySelector('script[data-monocle-variants]');
      if (jsonEl) {
        try {
          window.__MONOCLE_VARIANTS__ = JSON.parse(jsonEl.textContent || "[]");
        } catch (e) {
          console.warn("[MONOCLE] variants JSON parse failed", e);
          window.__MONOCLE_VARIANTS__ = [];
        }
      } else {
        window.__MONOCLE_VARIANTS__ = [];
      }
    }
    var variants = window.__MONOCLE_VARIANTS__;
    window.__MONOCLE_VARIANT_PROOF__ = {
      variants: variants,
      selectVariantById: selectVariantById
    };
    console.log("[MONOCLE] Ready. " + variants.length + " variants loaded.");
  }());

  // ─── Init ─────────────────────────────────────────────────────
  lockAtc("Choose your lens path to continue");
  render();

}());
